import { IconButton } from '@/components/ui/IconButton';
import type { PaginationMeta } from '@/types/api.types';
import { cn } from '@/utils/cn';
import { formatCount } from '@/utils/text';

import styles from './Pagination.module.css';

export interface PaginationProps {
  meta: PaginationMeta | undefined;
  onPageChange: (page: number) => void;
  /** What the rows are, for "Showing 1–10 of 57 members". */
  noun?: string;
}

/** Page numbers to show: first, last, and the neighbours of the current page. */
function visiblePages(current: number, total: number): (number | 'gap')[] {
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter((page) => page >= 1 && page <= total).sort((a, b) => a - b);
  const result: (number | 'gap')[] = [];
  sorted.forEach((page, index) => {
    const previous = sorted[index - 1];
    if (previous !== undefined && page - previous > 1) result.push('gap');
    result.push(page);
  });
  return result;
}

export function Pagination({ meta, onPageChange, noun = 'results' }: PaginationProps) {
  // One page needs no bar: "Showing 1–5 of 5" only takes room.
  if (!meta || meta.totalPages <= 1) return null;

  const first = (meta.page - 1) * meta.pageSize + 1;
  const last = Math.min(meta.page * meta.pageSize, meta.total);

  return (
    <nav className={styles.bar} aria-label="Pagination">
      <p className={cn(styles.summary, 'tabular')}>
        Showing {formatCount(first)}–{formatCount(last)} of {formatCount(meta.total)} {noun}
      </p>
      <div className={styles.pages}>
        <IconButton
          icon="chevron-left"
          label="Previous page"
          size="sm"
          disabled={meta.page <= 1}
          onClick={() => {
            onPageChange(meta.page - 1);
          }}
        />
        {visiblePages(meta.page, meta.totalPages).map((page, index) =>
          page === 'gap' ? (
            <span key={`gap-${String(index)}`} className={styles.gap}>
              …
            </span>
          ) : (
            <button
              key={page}
              type="button"
              className={cn(styles.page, 'tabular', page === meta.page && styles.current)}
              aria-current={page === meta.page ? 'page' : undefined}
              onClick={() => {
                onPageChange(page);
              }}
            >
              {page}
            </button>
          ),
        )}
        <IconButton
          icon="chevron-right"
          label="Next page"
          size="sm"
          disabled={meta.page >= meta.totalPages}
          onClick={() => {
            onPageChange(meta.page + 1);
          }}
        />
      </div>
    </nav>
  );
}
